import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { ArenaxWordmark } from './ArenaxWordmark';
import { toast } from 'sonner';
import { Mail, Lock, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';

/**
 * Phase 117 — Two-path ArenaX sign-in.
 *
 *   Section 1 (top, primary):    EXISTING OUT-PLAY USER  →  email + password
 *   Section 2 (below, secondary): NEW TO ARENAX          →  Continue with Google
 *
 * The previous version inverted this — Google was on top and email was a
 * fallback.  The new framing matches the CEO's unified-identity doctrine:
 * Out-Play credentials are honored first; Google is for fresh public users.
 *
 * Every surface color comes from the ArenaX theme CSS variables in
 * `arenax-theme.css` so the page flips cleanly under the navbar 🌙/☀️ toggle.
 */
export default function ArenaxLogin() {
  const { setUser, setToken } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [routingToGoogle, setRoutingToGoogle] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email and password required'); return; }
    setLoading(true);
    try {
      const r = await api.post('/arenax/auth/login', { email, password });
      const { access_token, user, federated } = r.data;
      localStorage.setItem('outplay_token', access_token);
      localStorage.setItem('outplay_user', JSON.stringify(user));
      setToken(access_token);
      setUser(user);
      toast.success(
        federated
          ? `Welcome back to ArenaX, ${user.name}`
          : `Welcome back, ${user.name}!`,
      );
      // Federated Out-Play users land on ArenaX home (not their native
      // dashboard) — their original role rides silently in the session.
      navigate(federated ? '/arenax' : '/arenax/player');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setRoutingToGoogle(true);
    const redirectUrl = window.location.origin + '/arenax/auth/callback';
    // brief beat so the branded interstitial registers before we leave the SPA
    setTimeout(() => {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 650);
  };

  return (
    <ArenaxLayout>
      {/* Phase 124 — branded interstitial before Emergent OAuth redirect.
          Emergent's OAuth surface ("LOG IN TO Out Play") is outside our
          control, but we own the moment of context-switch — show the
          ArenaX wordmark + identity attribution before the SPA leaves. */}
      {routingToGoogle && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-5 arenax-themed"
          style={{ background: 'var(--ax-bg-gradient)' }}
          data-testid="arenax-google-interstitial"
        >
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center mb-5">
              <ArenaxWordmark size="xl" />
            </div>
            <div
              className="text-[10px] uppercase tracking-[0.4em] mb-3 font-semibold"
              style={{ color: `hsl(var(--ax-text-faint))` }}
            >
              SIGNING IN TO ARENAX
            </div>
            <p className="text-sm mb-2" style={{ color: `hsl(var(--ax-text-muted))` }}>
              Routing securely through Google identity…
            </p>
            <p className="text-[11px] italic mb-6" style={{ color: `hsl(var(--ax-text-faint))` }}>
              Powered securely by Out-Play Identity
            </p>
            <Loader2 className="h-6 w-6 mx-auto animate-spin"
                     style={{ color: `hsl(var(--ax-accent-cyan))` }} />
          </div>
        </div>
      )}

      <section className="min-h-[80vh] flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md arenax-themed" data-testid="arenax-login-page">
          {/* Brand mark */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <ArenaxWordmark size="xl" />
            </div>
            <p
              className="text-sm mt-2"
              style={{ color: `hsl(var(--ax-text-muted))` }}
            >
              The city pulse, your player hub — both, one identity.
            </p>
          </div>

          {/* ============ SECTION 1 — Existing Out-Play User ============ */}
          <div
            className="rounded-2xl p-6 sm:p-7 border backdrop-blur-sm"
            style={{
              background: 'var(--ax-card-gradient)',
              borderColor: 'var(--ax-border-bold)',
            }}
            data-testid="arenax-login-existing-section"
          >
            <div className="flex items-start gap-2 mb-5">
              <ShieldCheck
                className="h-4 w-4 mt-0.5"
                style={{ color: `hsl(var(--ax-accent-cyan))` }}
              />
              <div>
                <h2
                  className="text-sm font-bold tracking-wide"
                  style={{ color: `hsl(var(--ax-text))` }}
                >
                  Existing Out-Play user
                </h2>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: `hsl(var(--ax-text-muted))` }}
                >
                  Sign in with your Out-Play credentials — role preserved.
                </p>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label
                  className="text-[11px] tracking-wider uppercase font-bold"
                  style={{ color: `hsl(var(--ax-text-muted))` }}
                >
                  Email
                </label>
                <div className="mt-1.5 relative">
                  <Mail
                    className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: `hsl(var(--ax-text-faint))` }}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    data-testid="arenax-login-email"
                    className="arenax-input w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                    placeholder="you@academy.in"
                  />
                </div>
              </div>
              <div>
                <label
                  className="text-[11px] tracking-wider uppercase font-bold"
                  style={{ color: `hsl(var(--ax-text-muted))` }}
                >
                  Password
                </label>
                <div className="mt-1.5 relative">
                  <Lock
                    className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: `hsl(var(--ax-text-faint))` }}
                  />
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    data-testid="arenax-login-password"
                    className="arenax-input w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="arenax-login-submit"
                className="w-full px-4 py-3 rounded-xl font-bold text-sm shadow-xl flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                style={{
                  background: 'var(--ax-primary-gradient)',
                  color: `hsl(var(--ax-button-text))`,
                  boxShadow: 'var(--ax-shadow-glow)',
                }}
              >
                {loading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <>Sign in <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          </div>

          {/* ============ Typographic divider ============ */}
          <div className="relative my-7 flex items-center" data-testid="arenax-login-divider">
            <div className="flex-1 h-px" style={{ background: 'var(--ax-divider)' }} />
            <span
              className="px-4 text-[10px] tracking-[0.4em] font-semibold"
              style={{ color: `hsl(var(--ax-text-faint))` }}
            >
              OR
            </span>
            <div className="flex-1 h-px" style={{ background: 'var(--ax-divider)' }} />
          </div>

          {/* ============ SECTION 2 — New to ArenaX ============ */}
          <div
            className="rounded-2xl p-6 sm:p-7 border"
            style={{
              background: 'var(--ax-card-bg-soft)',
              borderColor: 'var(--ax-border-quiet)',
            }}
            data-testid="arenax-login-new-section"
          >
            <div className="mb-4">
              <h2
                className="text-sm font-bold tracking-wide"
                style={{ color: `hsl(var(--ax-text))` }}
              >
                New to ArenaX?
              </h2>
              <p
                className="text-xs mt-0.5"
                style={{ color: `hsl(var(--ax-text-muted))` }}
              >
                Continue with Google to create your player profile in seconds.
              </p>
            </div>

            <button
              onClick={onGoogleLogin}
              data-testid="arenax-login-google"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5"
              style={{
                background: 'hsl(var(--ax-bg-elevated))',
                color: `hsl(var(--ax-text))`,
                border: '1px solid var(--ax-input-border)',
              }}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.22-4.74 3.22-8.32z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.87 0-5.29-1.94-6.16-4.53H2.18v2.84A10.99 10.99 0 0012 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 010-4.18V7.07H2.18a10.99 10.99 0 000 9.86l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A10.99 10.99 0 002.18 7.07l3.66 2.84C6.71 7.32 9.13 5.38 12 5.38z"/>
              </svg>
              Continue with Google
            </button>

            <p
              className="text-[11px] text-center mt-3"
              style={{ color: `hsl(var(--ax-text-faint))` }}
            >
              Prefer email signup?{' '}
              <a
                href="/arenax/auth/register"
                className="font-semibold hover:underline"
                style={{ color: `hsl(var(--ax-accent-cyan))` }}
                data-testid="arenax-login-register-link"
              >
                Create an account
              </a>
            </p>
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}
